//@flow
import React from 'react';
import {connect} from 'react-redux';
import {makeStyles, Typography} from '@material-ui/core';

import CustomModal from '../../../../elements/CustomModal/CustomModal';
import PromotionItem from './PromotionItem';

import DeliveryPromotion from './DeliveryPromotion';
import {getTerms} from './utils';

import {colors} from '../../../../assets/styles/colors';
import {commaFormat} from '../../../../utils/number';

import type {
  SinglePromotion,
  Currency,
  ShoppingCartItem,
} from '../../../../entities';
import type {RootState} from '../../../index';
import type {MiniPromotion} from '../../duck';

type PromotionModalProps = {
  promotions: SinglePromotion[],
  open: boolean,
  onClose: () => void,
  currency: Currency,
  onViewProducts: (promotionId: string) => void,

  shoppingCartProducts: ShoppingCartItem[],
  shoppingCartSubtotal?: string,
  promotionsApplied?: MiniPromotion[],
};

const useStyle = makeStyles(theme => ({
  modal: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: 600,
  },
  promotionDescription: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.grey800,
  },
  promotionHighLigh: {
    color: colors.purple,
  },
}));

function PromotionModal(props: PromotionModalProps) {
  const {
    promotions,
    onClose,
    open,
    currency,
    onViewProducts,
    shoppingCartProducts,
    shoppingCartSubtotal = '0',
    promotionsApplied,
  } = props;
  const classes = useStyle();

  return (
    <CustomModal
      open={open}
      handleCloseModal={onClose}
      className={classes.modal}
    >
      <Typography className={classes.modalTitle} align="center">
        ¡No te pierdas estas promociones!
      </Typography>
      <DeliveryPromotion currency={currency} />
      {promotions
        .filter(p => p.enabled)
        .map(promotion => {
          const products = shoppingCartProducts.filter(item => {
            const productHasPromotion = item.product.promotions;
            if (!productHasPromotion) return false;
            return item.product.promotions.some(
              innerPromotion => innerPromotion.slug === promotion.slug
            );
          });

          const totalProducts = products.reduce((acc, product) => {
            return acc + product.amount;
          }, 0);

          const thisPromotionIsApplied =
            promotionsApplied &&
            promotionsApplied.some(promo => {
              return promo.promotion === promotion.id;
            });

          let description;
          let percentageProgress = null;
          if (promotion.promotionMode === 'PRODUCT') {
            if (promotion.promotionType === 'BUYANDPAY') {
              const restProducts = promotion.buyQuantity - totalProducts;

              const appliedMessage = thisPromotionIsApplied
                ? 'aplicada.'
                : 'no se aplicó, revise los terminos y condiciones.';

              percentageProgress =
                (totalProducts / promotion.buyQuantity) * 100;
              if (restProducts <= 0) {
                // applied message
                description = (
                  <Typography className={classes.promotionDescription}>
                    Promoción {promotion.buyQuantity}x{promotion.payQuantity}{' '}
                    <span className={classes.promotionHighLigh}>
                      {appliedMessage}
                    </span>
                  </Typography>
                );
              } else {
                description = (
                  <Typography className={classes.promotionDescription}>
                    Agrega{' '}
                    <span className={classes.promotionHighLigh}>
                      {restProducts}
                      {restProducts === 1 ? ' producto' : ' productos'}
                    </span>{' '}
                    y accede la promoción {promotion.buyQuantity}x
                    {promotion.payQuantity}, en productos especificos.
                  </Typography>
                );
              }
            }

            if (promotion.promotionType === 'PERCENTAGE') {
              const appliedMessage = thisPromotionIsApplied
                ? 'aplicado.'
                : 'no se aplicó, revise los terminos y condiciones.';

              const discountPercentage =
                Number(promotion.discountPercentage) || 0;

              if (promotion.minAmount !== null) {
                const restAmount =
                  Number(promotion.minAmount) - Number(shoppingCartSubtotal);
                percentageProgress =
                  (1 - restAmount / promotion.minAmount) * 100;

                if (restAmount <= 0) {
                  description = (
                    <Typography className={classes.promotionDescription}>
                      Descuento de {discountPercentage}%
                      <span className={classes.promotionHighLigh}>
                        {' '}
                        {appliedMessage}
                      </span>
                    </Typography>
                  );
                } else {
                  description = (
                    <Typography className={classes.promotionDescription}>
                      Agrega{' '}
                      <span className={classes.promotionHighLigh}>
                        {commaFormat(restAmount, currency.symbol)}
                      </span>{' '}
                      en productos y accede a un {discountPercentage}% de
                      descuento solo aplican a productos especificos.
                    </Typography>
                  );
                }
              } else {
                description = (
                  <Typography className={classes.promotionDescription}>
                    Tienes un descuento de{' '}
                    <span className={classes.promotionHighLigh}>
                      {discountPercentage}%{' '}
                      {thisPromotionIsApplied && 'Aplicado'}
                      {products.length > 0 &&
                        !thisPromotionIsApplied &&
                        'no aplicado, revise los terminos y condiciones.'}
                    </span>{' '}
                    {products.length === 0 && 'en productos especificos.'}
                  </Typography>
                );
              }
            }
          }

          if (promotion.promotionMode === 'CATEGORY') {
            if (promotion.promotionType === 'BUYANDPAY') {
              const restProducts = promotion.buyQuantity
                ? promotion.buyQuantity - totalProducts
                : 0;
              percentageProgress = promotion.buyQuantity
                ? (totalProducts / promotion.buyQuantity) * 100
                : 0;

              const categories = promotion.productCategories
                ? promotion.productCategories.map(c => c.productCategory.name)
                : [];

              const appliedMessage = thisPromotionIsApplied
                ? 'aplicada.'
                : 'no se aplicó, revise los terminos y condiciones.';

              if (restProducts <= 0) {
                // applied message
                description = (
                  <Typography className={classes.promotionDescription}>
                    Promoción {promotion.buyQuantity}x{promotion.payQuantity}
                    {categories.length > 1
                      ? ' en categorías '
                      : ' en la categoría '}
                    {categories.join(', ')}{' '}
                    <span className={classes.promotionHighLigh}>
                      {appliedMessage}
                    </span>
                  </Typography>
                );
              } else {
                description = (
                  <Typography className={classes.promotionDescription}>
                    Agrega{' '}
                    <span className={classes.promotionHighLigh}>
                      {restProducts}
                      {restProducts === 1 ? ' producto' : ' productos'}
                    </span>{' '}
                    {categories.length > 1
                      ? ' de las categorías '
                      : ' de la categoría '}
                    {categories.join(', ')} y accede la promoción{' '}
                    {promotion.buyQuantity}x{promotion.payQuantity}.
                  </Typography>
                );
              }
            }

            if (promotion.promotionType === 'PERCENTAGE') {
              const discountPercentage =
                Number(promotion.discountPercentage) || 0;

              if (promotion.minAmount !== null) {
                const restAmount =
                  Number(promotion.minAmount) - Number(shoppingCartSubtotal);

                percentageProgress =
                  (1 - restAmount / Number(promotion.minAmount)) * 100;

                const categories = promotion.productCategories
                  ? promotion.productCategories.map(c => c.productCategory.name)
                  : [];

                const appliedMessage = thisPromotionIsApplied
                  ? 'aplicado.'
                  : 'no se aplicó, revise los terminos y condiciones.';

                if (restAmount <= 0) {
                  description = (
                    <Typography className={classes.promotionDescription}>
                      Descuento de {discountPercentage}%
                      {categories.length > 1
                        ? ' en las categorías '
                        : ' en la categoría '}
                      {categories.join(', ')}
                      <span className={classes.promotionHighLigh}>
                        {appliedMessage}
                      </span>
                    </Typography>
                  );
                } else {
                  description = (
                    <Typography className={classes.promotionDescription}>
                      Agrega{' '}
                      <span className={classes.promotionHighLigh}>
                        {commaFormat(restAmount, currency.symbol)}
                      </span>{' '}
                      en productos
                      {categories.length > 1
                        ? ' de las categorías '
                        : ' de la categoría '}
                      {categories.join(', ')} y obtén {discountPercentage}% de
                      dscto!.
                    </Typography>
                  );
                }
              } else {
                description = (
                  <Typography className={classes.promotionDescription}>
                    Tienes un descuento de{' '}
                    <span className={classes.promotionHighLigh}>
                      {discountPercentage}%{' '}
                      {thisPromotionIsApplied && 'Aplicado'}
                      {products.length > 0 &&
                        !thisPromotionIsApplied &&
                        'no aplicado, revise los terminos y condiciones.'}
                    </span>{' '}
                    {products.length === 0 && (
                      <>
                        en{' '}
                        {promotion.productCategories.length > 1
                          ? 'las siguientes categorías: '
                          : 'la catergoría '}
                        {promotion.productCategories
                          .map(category => category.productCategory.name)
                          .join(', ')}
                        .
                      </>
                    )}
                  </Typography>
                );
              }
            }
          }

          return (
            <PromotionItem
              key={promotion.id}
              promotion={promotion}
              productsInShoppingCart={products}
              currency={currency}
              terms={`${getTerms(promotion, currency)}.`}
              description={description}
              percentageProgress={percentageProgress}
              onViewProducts={onViewProducts}
              promotionScope={promotion.promotionScope}
            />
          );
        })}
    </CustomModal>
  );
}

export default connect((state: RootState) => ({
  shoppingCartProducts:
    state.manualOrder.shoppingCart && state.manualOrder.shoppingCart.items,
  shoppingCartSubtotal: state.manualOrder.subTotal,
  promotionsApplied: state.manualOrder.promotions,
}))(PromotionModal);
